import {
    FC,
    ReactElement,
    useMemo,
    useState,
} from 'react'
import classNames from 'classnames'

import { Icon } from '../../../components'
import { ConnectedEditShiftTimeForm } from '../../../connectors'
import { Exchange, ShiftExchange } from '../../../entities/Exchange/Exchange'
import { ShiftIndex } from '../../../entities/Shift/Shift'
import { ShiftsCalendarAbsence } from '../../../entities/ShiftsCalendarAbsence/ShiftsCalendarAbsence'
import { ShiftsCalendarLeaveOfAbsence } from '../../../entities/ShiftsCalendarLeaveOfAbsence/ShiftsCalendarLeaveOfAbsence'
import { ShiftsCalendarUnavailableToWorkTimeSlot } from '../../../entities/ShiftsCalendarUnavailableToWorkTimeSlot/ShiftsCalendarUnavailableToWorkTimeSlot'
import { getPersonFullName } from '../../../helpers'
import trans from '../../../helpers/trans'
import {
    CalendarShiftEmployee,
    CalendarShiftPlanning,
    CalendarShiftTemporaryWorker,
    EditTimeButton,
} from '../../index'

import {
    getCalendarShiftStatus,
    getRegisteredShiftPlanningsText,
    isUserInExchangeRequests,
    isUserInShiftExchanges,
} from './helpers'
import './CalendarShift.scss'

interface CalendarShiftProps {
    exchangeRequests: Exchange[];
    shiftExchanges: ShiftExchange[];
    canEditShift?: boolean;
    draggable?: boolean;
    isPreview?: boolean;
    showEmployeeEmploymentType?: boolean;
    absences: ShiftsCalendarAbsence[];
    leaveOfAbsences: ShiftsCalendarLeaveOfAbsence[];
    shift: ShiftIndex;
    unavailableToWorkTimeSlots: ShiftsCalendarUnavailableToWorkTimeSlot[];
    onClick: (shift: ShiftIndex) => void;
    onDragStart?: (shift: ShiftIndex) => void;
    onDragEnd?: () => void;
    className?: string;
}

export enum ShiftStatusType {
    past = 'past',
    full = 'full',
    available = 'available'
}

const CalendarShift: FC<CalendarShiftProps> = ({
    exchangeRequests,
    shiftExchanges,
    canEditShift,
    draggable,
    isPreview,
    showEmployeeEmploymentType,
    absences,
    leaveOfAbsences,
    shift,
    unavailableToWorkTimeSlots,
    onClick,
    onDragStart,
    onDragEnd,
    className = '',
}): ReactElement => {
    const [showEditTimeForm, setShowEditTimeForm] = useState(false)

    const plannedShiftPlannings = useMemo(() => shift.shiftPlannings.filter((shiftPlanning) => shiftPlanning.planned), [shift])
    const registeredShiftPlanningsText = useMemo(() => getRegisteredShiftPlanningsText(shift.shiftPlannings), [shift])
    const { previouslyPlannedUsers } = shift
    const shiftStatus = useMemo(() => getCalendarShiftStatus(shift), [shift])
    const showRegisteredShiftPlanningsText = shiftStatus !== ShiftStatusType.past && registeredShiftPlanningsText && ! isPreview
    const plannedEmployeesCount = plannedShiftPlannings.length + shift.temporaryWorkers.length

    const elementClassName = classNames('calendar-shift', {
        [`calendar-shift--is-${shiftStatus}`]: shiftStatus,
        'calendar-shift--is-preview': isPreview,
        'calendar-shift--show-registered-shift-plannings': showRegisteredShiftPlanningsText,
    }, className)

    const { name: departmentName, departmentGroup } = shift.department
    const departmentClassNames = classNames('calendar-shift__department', {
        'calendar-shift__department--is-bold': ! departmentGroup,
    })

    const handleClick = (): void => {
        onClick(shift)
    }

    const handleDragStart = (): void => {
        if (onDragStart) {
            onDragStart(shift)
        }
    }

    const handleTimeButtonClick = (): void => {
        setShowEditTimeForm(true)
    }

    const handleCloseSubmitEditShiftTimeForm = (): void => {
        setShowEditTimeForm(false)
    }

    return (
        <div
            draggable={draggable}
            onDragStart={handleDragStart}
            onDragEnd={onDragEnd}
            className={elementClassName}
        >
            <div className="calendar-shift__main-info">
                <div className="calendar-shift__time-and-people-count">
                    {showEditTimeForm ? (
                        <ConnectedEditShiftTimeForm
                            shift={shift}
                            onClose={handleCloseSubmitEditShiftTimeForm}
                            className="calendar-shift__edit-time-form"
                        />
                    ) : (
                        <EditTimeButton
                            isDisabled={! canEditShift}
                            period={shift.period}
                            onClick={handleTimeButtonClick}
                            className="calendar-shift__edit-time-button"
                        />
                    )}
                    <div className="calendar-shift__people-count">
                        {`${plannedEmployeesCount}/${shift.peopleLimit}`}
                    </div>
                </div>

                <div className={departmentClassNames}>
                    {departmentGroup && (
                        <span className="calendar-shift__department-group-name">
                            {`${departmentGroup.name} -`}
                        </span>
                    )}
                    {departmentName}
                </div>

                {(!! (plannedEmployeesCount || previouslyPlannedUsers.length)) && (
                    <ul className="calendar-shift__planned-employees">
                        {plannedShiftPlannings.map(shiftPlanning => (
                            <CalendarShiftPlanning
                                exchangeRequests={isUserInExchangeRequests(exchangeRequests, shiftPlanning)}
                                shiftExchanges={isUserInShiftExchanges(shiftExchanges, shiftPlanning)}
                                isPreview={isPreview}
                                showEmploymentType={showEmployeeEmploymentType}
                                absences={absences}
                                color={shiftPlanning.color}
                                key={shiftPlanning.id}
                                leaveOfAbsences={leaveOfAbsences}
                                shiftPeriod={shift.period}
                                shiftPlanning={shiftPlanning}
                                unavailableToWorkTimeSlots={unavailableToWorkTimeSlots}
                                employmentTypeClassName="calendar-shift__employment-type"
                                iconClassName="calendar-shift__employee-icon"
                            />
                        ))}
                        {shift.temporaryWorkers.map(temporaryWorker => (
                            <CalendarShiftTemporaryWorker
                                showEmploymentType={showEmployeeEmploymentType}
                                key={temporaryWorker.id}
                                shiftPeriod={shift.period}
                                temporaryWorker={temporaryWorker}
                                employmentTypeClassName="calendar-shift__employment-type"
                                iconClassName="calendar-shift__employee-icon"
                            />
                        ))}
                        {previouslyPlannedUsers.map(previouslyPlannedUser => (
                            <CalendarShiftEmployee
                                isPreviouslyPlannedUser
                                key={previouslyPlannedUser.id}
                                name={getPersonFullName(previouslyPlannedUser.person)}
                                color={null}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {showRegisteredShiftPlanningsText && (
                <div className="calendar-shift__registered-employees-info">
                    {registeredShiftPlanningsText}
                </div>
            )}

            {draggable && <Icon name="draggable" className="calendar-shift__draggable-icon" />}

            {/* Fix for FF: Element can not be draggable if there's a button inside of it */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div
                aria-label={trans('common.shift')}
                onClick={handleClick}
                className="calendar-shift__button"
            />
        </div>
    )
}

export default CalendarShift
